import {
  Button,
  colors,
  Icon,
  IconButton,
  type IconName,
  Link,
  Modal,
  Tag,
  type TagVariant,
  Tooltip,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { type PostV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest } from 'src/client/spendesk-api-types';
import { type Field } from 'src/core/common/components/FilterBuilder';
import { toFilterUrlParameter } from 'src/core/common/components/FilterBuilder/FilterBuilderModel';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications/hooks/useNotifications';
import {
  useCreateSpainClosurePeriod,
  useDownloadSpainClosurePeriod,
  useRetrySpainClosurePeriod,
} from 'src/core/modules/bookkeep/apis/useSpainClosurePeriods';
import { useGetFields } from 'src/core/modules/payable/components';

import ellipseIcon from './assets/icons/ellipse.svg';
import { type SanitizedSpainClosurePeriod } from './helpers/sanitizePeriodsList';

const StatusIcon = ({
  state,
}: {
  state: SanitizedSpainClosurePeriod['state'];
}) => {
  if (state === 'completed') {
    return (
      <Icon name="circle-check" size="m" color={colors.contentSuccessDefault} />
    );
  }
  return <img src={ellipseIcon} alt="" className="w-[16px] object-contain" />;
};

const StatusTag = ({
  state,
}: {
  state: SanitizedSpainClosurePeriod['state'];
}) => {
  const { t } = useTranslation('global');
  const statusTagMap: Partial<
    Record<
      SanitizedSpainClosurePeriod['state'],
      {
        iconName?: IconName;
        variant: TagVariant;
        text: string;
        tooltipContent?: string;
      }
    >
  > = {
    completed: {
      iconName: 'lock-close',
      variant: 'success',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archived'),
    },
    ongoing: {
      iconName: 'clock-filled',
      variant: 'info',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archivingInProgress'),
    },
    pending: {
      iconName: 'clock-filled',
      variant: 'info',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archivingInProgress'),
    },
    current: {
      variant: 'purple',
      text: t('bookkeep.settings.spanishDpr.periods.tags.currentYear'),
      tooltipContent: t(
        'bookkeep.settings.spanishDpr.periods.tags.cannotArchiveCurrentYear',
      ),
    },
  };

  const statusTag = statusTagMap[state];
  if (!statusTag) {
    return null;
  }

  if (statusTag.tooltipContent) {
    return (
      <Tooltip content={statusTag.tooltipContent}>
        <Tag
          iconName={statusTag.iconName}
          variant={statusTag.variant}
          className="cursor-pointer"
        >
          {statusTag.text}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tag
      iconName={statusTag.iconName}
      variant={statusTag.variant}
      className="cursor-pointer"
    >
      {statusTag.text}
    </Tag>
  );
};

const ActionButton = ({
  period,
  isPending,
  setIsArchiveConfirmModalOpen,
}: {
  period: SanitizedSpainClosurePeriod;
  isPending: boolean;
  setIsArchiveConfirmModalOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useTranslation('global');
  const { downloadPeriod, isPending: isDownloadPending } =
    useDownloadSpainClosurePeriod();
  const { pushNotif } = useNotifications();
  const periodId = period.id;
  if (period.state === 'completed' && periodId) {
    return (
      <Tooltip
        content={t(
          'bookkeep.settings.spanishDpr.periods.buttons.archivedDownloadTooltip',
        )}
      >
        <IconButton
          iconName="arrow-down-tray"
          variant="secondaryNeutral"
          aria-label="Download"
          onClick={() =>
            downloadPeriod(periodId, {
              onSuccess: () => {
                pushNotif({
                  type: NotificationType.Success,
                  message: t(
                    'bookkeep.settings.spanishDpr.periods.success.downloadStarted',
                  ),
                });
              },
              onError: () => {
                pushNotif({
                  type: NotificationType.Danger,
                  message: t(
                    'bookkeep.settings.spanishDpr.periods.error.downloadFailed',
                  ),
                });
              },
            })
          }
          isDisabled={isDownloadPending}
        />
      </Tooltip>
    );
  }

  if (period.state === 'to_process' || period.state === 'failed') {
    if (period.readyToArchive) {
      return (
        <Button
          variant="secondaryNeutral"
          text={t('bookkeep.settings.spanishDpr.periods.buttons.archive')}
          onClick={() => setIsArchiveConfirmModalOpen(true)}
          isDisabled={isPending}
        />
      );
    }
    return (
      <Tooltip
        content={t(
          'bookkeep.settings.spanishDpr.periods.buttons.archiveNotAvailableTooltip',
        )}
      >
        <Button
          variant="secondaryNeutral"
          text={t('bookkeep.settings.spanishDpr.periods.buttons.archive')}
          isDisabled
        />
      </Tooltip>
    );
  }
  return null;
};

const generateDefaultErrorMessage = (period: SanitizedSpainClosurePeriod) => {
  if (
    period.lastError &&
    period.lastError.step === 'preflightValidation' &&
    period.failedPayablesCount &&
    period.failedPayablesCount > 0
  ) {
    return period.lastError.step;
  }
  // TODO: add partner error message
  // if (period.lastError && period.lastError.step === 'partnerError') {
  //   return period.lastError.step;
  // }
  return undefined;
};

const generatePayablesUrl = (
  period: SanitizedSpainClosurePeriod,
  companyId: string,
  fields: Field[],
  errorMessage: string | undefined,
) => {
  if (!errorMessage) {
    return undefined;
  }
  if (
    errorMessage === 'preflightValidation' &&
    period.failedPayablesCount &&
    period.failedPayablesCount > 0
  ) {
    const fromDate = period.from.split('T')[0];
    const toDate = period.to.split('T')[0];
    const filter = {
      operator: 'and' as const,
      subfilters: [
        {
          operator: 'and' as const,
          subfilters: [
            {
              field: 'documentaryEvidenceStatus',
              operator: 'is',
              value: ['hasDprError'] as [string, ...string[]],
            },
            {
              field: 'payableDate',
              operator: 'isBetween',
              value: {
                from: fromDate,
                to: toDate,
              },
            },
          ],
        },
      ],
    };
    const filterParameter = toFilterUrlParameter(
      { filter, name: '', id: undefined },
      fields,
    );
    return `${routeFor(routes.PAYABLES_ALL.path, { company: companyId })}?payableFilter=${filterParameter}`;
  }
  if (errorMessage === 'UnexportedPayablesInRange') {
    const fromDate = period.from.split('T')[0];
    const toDate = period.to.split('T')[0];
    const filter = {
      operator: 'and' as const,
      subfilters: [
        {
          operator: 'and' as const,
          subfilters: [
            {
              field: 'bookkeepingStatus',
              operator: 'isNot',
              value: ['exported'] as [string, ...string[]],
            },
            {
              field: 'documentaryEvidenceStatus',
              operator: 'is',
              value: ['providedAndDpr'] as [string, ...string[]],
            },
            {
              field: 'payableDate',
              operator: 'isBetween',
              value: {
                from: fromDate,
                to: toDate,
              },
            },
          ],
        },
      ],
    };
    const filterParameter = toFilterUrlParameter(
      { filter, name: '', id: undefined },
      fields,
    );
    return `${routeFor(routes.PAYABLES_ALL.path, { company: companyId })}?payableFilter=${filterParameter}&spainDprUnexportedPayablesError=1`;
  }
  return undefined;
};

export function SpanishDprPeriod({
  period,
  className,
}: {
  className?: string;
  period: SanitizedSpainClosurePeriod;
}) {
  const { activeLanguage, t } = useTranslation('global');
  const companyId = useCompanyId();
  const formatter = new Intl.DateTimeFormat(activeLanguage, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const formattedFromDate = formatter.format(new Date(period.from));
  const formattedToDate = formatter.format(new Date(period.to));

  const formattedPeriodDates = `${formattedFromDate} - ${formattedToDate}`;

  const { createPeriod, isPending: isCreatePending } =
    useCreateSpainClosurePeriod();
  const { retryPeriod, isPending: isRetryPending } =
    useRetrySpainClosurePeriod();
  const isPending = isCreatePending || isRetryPending;
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] =
    useState(false);
  const [isArchiveSubmittedModalOpen, setIsArchiveSubmittedModalOpen] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    generateDefaultErrorMessage(period),
  );

  const fields = useGetFields({ variant: 'all-payables' });
  const errorMessageExternalLink = useMemo(
    () => generatePayablesUrl(period, companyId, fields, errorMessage),
    [period, companyId, fields, errorMessage],
  );

  const handleArchive = () => {
    const fromDate = new Date(period.from).toISOString().split('T')[0];
    const toDate = new Date(period.to).toISOString().split('T')[0];

    const dataToArchive: PostV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest['body'] =
      { from: fromDate, to: toDate };

    setErrorMessage(undefined);

    if (period.state === 'failed' && period.id) {
      retryPeriod(period.id, {
        onSuccess: () => {
          setIsArchiveConfirmModalOpen(false);
          setIsArchiveSubmittedModalOpen(true);
        },
        onError: (error: unknown) => {
          setErrorMessage((error as Error).message as string);
          setIsArchiveConfirmModalOpen(false);
        },
      });
      return;
    }
    createPeriod(dataToArchive, {
      onSuccess: () => {
        setIsArchiveConfirmModalOpen(false);
        setIsArchiveSubmittedModalOpen(true);
      },
      onError: (error: unknown) => {
        setErrorMessage((error as Error).message as string);
        setIsArchiveConfirmModalOpen(false);
      },
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    if (errorMessage === 'UnexportedPayablesInRange') {
      return (
        <Trans i18nKey="bookkeep.settings.spanishDpr.periods.error.unexportedPayablesInRange">
          <Link
            href={errorMessageExternalLink ?? ''}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Trans>
      );
    }

    if (
      errorMessage === 'preflightValidation' &&
      'failedPayablesCount' in period &&
      period.failedPayablesCount &&
      period.failedPayablesCount > 0
    ) {
      return (
        <Trans
          i18nKey="bookkeep.settings.spanishDpr.periods.error.preflightValidation"
          values={{
            failedPayablesCount: period.failedPayablesCount,
            payable:
              period.failedPayablesCount > 1
                ? t('bookkeep.settings.spanishDpr.periods.error.payable_plural')
                : t('bookkeep.settings.spanishDpr.periods.error.payable'),
          }}
        >
          <Link
            href={errorMessageExternalLink ?? ''}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Trans>
      );
    }

    return (
      <Trans i18nKey="bookkeep.settings.spanishDpr.periods.error.default">
        <Link
          as="a"
          href="#"
          id="intercom-message"
          onClick={() => window.Intercom('show')}
        >
          -
        </Link>
      </Trans>
    );
  };

  return (
    <>
      {/* Archive confirm modal */}
      <Modal
        isOpen={isArchiveConfirmModalOpen}
        onClose={() => setIsArchiveConfirmModalOpen(false)}
        title={t(
          'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.title',
        )}
        iconName="triangle-warning"
        iconVariant="warning"
        actions={[
          <Button
            key="cancel"
            variant="secondaryNeutral"
            text={t('misc.cancel')}
            onClick={() => setIsArchiveConfirmModalOpen(false)}
          />,
          <Button
            key="archive"
            variant="primaryWarning"
            text={t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.archiveButton',
            )}
            onClick={handleArchive}
            isDisabled={isPending}
            isLoading={isPending}
          />,
        ]}
      >
        <ol className="body-m text-content-primary-default flex list-outside flex-col items-start gap-16 px-12 py-24 text-left">
          <li>
            {t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.content1',
              {
                from: formattedFromDate,
                to: formattedToDate,
              },
            )}
          </li>
          <li>
            {t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.content2',
            )}
          </li>
          <li>
            {t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.content3',
            )}
          </li>
        </ol>
      </Modal>

      {/* Archive submitted modal */}
      <Modal
        isOpen={isArchiveSubmittedModalOpen}
        onClose={() => setIsArchiveSubmittedModalOpen(false)}
        title={t(
          'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.title',
        )}
        iconName="clock-filled"
        iconVariant="info"
        actions={[
          <Button
            key="gotIt"
            variant="primaryInfo"
            text={t(
              'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.gotItButton',
            )}
            onClick={() => setIsArchiveSubmittedModalOpen(false)}
          />,
        ]}
      >
        {t(
          'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.content',
          {
            from: formattedFromDate,
            to: formattedToDate,
          },
        )}
      </Modal>

      <div
        className={classNames(
          'align-self-stretch border-border-default bg-background-primary-default flex items-start justify-center gap-12 px-16 py-12',
          { 'bg-background-secondary-alert-default': errorMessage },
          className,
        )}
      >
        <div className="flex h-full w-full items-center justify-between">
          <div className="flex flex-col items-start gap-8">
            <div className="flex items-center gap-8">
              <StatusIcon state={period.state} />
              <p className="body-m text-content-primary-default">
                {formattedPeriodDates}
              </p>
            </div>
            {errorMessage && (
              <div className="flex items-center gap-8">
                <Icon
                  name="hexagone-cross"
                  size="m"
                  color={colors.contentAlertDefault}
                />
                <p className="body-m text-content-alert-default">
                  {renderErrorMessage()}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-8">
            <StatusTag state={period.state} />
            <ActionButton
              period={period}
              setIsArchiveConfirmModalOpen={setIsArchiveConfirmModalOpen}
              isPending={isPending}
            />
          </div>
        </div>
      </div>
    </>
  );
}
